import axios from "axios";
import { getAuthToken } from "./cookieUtils";

const baseURL = process.env.REACT_APP_API_BASE_URL;

// Helper function to get auth headers
const getAuthHeaders = async (clientId = null, user = null) => {
  const token = await getAuthToken();
  const headers = {
    "x-api-key": process.env.REACT_APP_API_KEY,
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  
  // Use the provided clientId or get it from user data
  let finalClientId = clientId;
  if (!finalClientId && user) {
    if (user.role === 'super_admin') {
      // For super admin, we'll need to get client ID from a different source
      // For now, we'll use the user's client_id if available
      finalClientId = user.client_id || null;
    } else {
      // Regular users use their assigned client_id from auth
      finalClientId = user.client_id || null;
    }
  }
  
  if (finalClientId) {
    headers["X-Client-ID"] = finalClientId;
  }
  
  return headers;
};

// Login user
export const loginUser = async (credentials) => {
  try {
    console.log("🔐 Attempting login with credentials:", {
      username_or_email: credentials.usernameOrEmail,
      password: credentials.password ? '[HIDDEN]' : 'undefined'
    });
    
    const response = await axios.post(`${baseURL}/auth/login`, {
      username_or_email: credentials.usernameOrEmail,
      password: credentials.password
    }, {
      headers: {
        "x-api-key": process.env.REACT_APP_API_KEY,
        "Content-Type": "application/json",
      },
    });
    
    console.log("✅ Login Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Login Error:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      }
    });
    
    // Create a more informative error message
    let errorMessage = 'Login failed';
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 400) {
      // Check if it's a missing credentials error
      if (error.response?.data?.error?.includes('credentials') || 
          error.response?.data?.message?.includes('credentials')) {
        errorMessage = 'Please provide login credentials';
      } else {
        errorMessage = 'Invalid username or password';
      }
    } else if (error.response?.status === 401) {
      errorMessage = 'Authentication failed';
    } else if (error.response?.status === 500) {
      errorMessage = 'Server error. Please try again later.';
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    enhancedError.status = error.response?.status;
    enhancedError.data = error.response?.data;
    
    throw enhancedError;
  }
};

// Get current user
export const getCurrentUser = async (clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.get(`${baseURL}/auth/me`, { headers });
    
    console.log("Current User Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Get Current User Error:", error);
    throw error;
  }
};

// Validate token
export const validateToken = async (clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.get(`${baseURL}/auth/validate`, { headers });
    
    console.log("Token Validation Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Token Validation Error:", error);
    throw error;
  }
};

// Logout user
// export const logoutUser = async (clientId = null, user = null) => {
//   try {
//     const headers = await getAuthHeaders(clientId, user);
//     const response = await axios.post(`${baseURL}/auth/logout`, {}, { headers });
    
//     console.log("✅ Logout Response:-->", response.data);
//     return response.data;
//   } catch (error) {
//     console.error("❌ Logout Error:", {
//       message: error.message,
//       status: error.response?.status,
//       statusText: error.response?.statusText,
//       data: error.response?.data
//     });
    
//     // Don't throw error for logout - just log it and continue
//     // This prevents CORS issues from blocking the logout process
//     console.warn("⚠️ Logout API call failed, but continuing with local logout");
//     return { success: false, message: 'Logout API call failed, but local logout completed' };
//   }
// };

// Refresh token
export const refreshToken = async (clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.post(`${baseURL}/auth/refresh`, {}, { headers });
    
    console.log("Token Refresh Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Token Refresh Error:", error);
    throw error;
  }
};

// Get user roles
export const getUserRoles = async (clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.get(`${baseURL}/auth/roles`, { headers });
    
    console.log("User Roles Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Get User Roles Error:", error);
    throw error;
  }
};

// Get all users (admin only)
export const getAllUsers = async (clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.get(`${baseURL}/auth/users`, { headers });
    
    console.log("All Users Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Get All Users Error:", error);
    throw error;
  }
};

// Register new user (admin only)
export const registerUser = async (userData, clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.post(`${baseURL}/auth/register`, userData, { headers });
    
    console.log("Register User Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Register User Error:", error);
    throw error;
  }
};

// Update user role (admin only)
export const updateUserRole = async (userId, role, clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.put(`${baseURL}/auth/users/${userId}/role`, { role }, { headers });
    
    console.log("Update User Role Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Update User Role Error:", error);
    throw error;
  }
};

// Toggle user status (admin only)
export const toggleUserStatus = async (userId, clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.put(`${baseURL}/auth/users/${userId}/status`, {}, { headers });
    
    console.log("Toggle User Status Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Toggle User Status Error:", error);
    throw error;
  }
};

// Request password reset
export const requestPasswordReset = async (email) => {
  try {
    const response = await axios.post(`${baseURL}/auth/forgot-password`, { email }, {
      headers: {
        "x-api-key": process.env.REACT_APP_API_KEY,
        "Content-Type": "application/json",
      },
    });
    
    console.log("Password Reset Request Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Password Reset Request Error:", error);
    throw error;
  }
};

// Reset password with token
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await axios.post(`${baseURL}/auth/reset-password`, { 
      token, 
      new_password: newPassword 
    }, {
      headers: {
        "x-api-key": process.env.REACT_APP_API_KEY,
        "Content-Type": "application/json",
      },
    });
    
    console.log("Reset Password Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Reset Password Error:", error);
    throw error;
  }
};

// Change password
export const changePassword = async (currentPassword, newPassword, clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.put(`${baseURL}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    }, { headers });
    
    console.log("Change Password Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Change Password Error:", error);
    throw error;
  }
};

// Update user profile
export const updateUserProfile = async (profileData, clientId = null, user = null) => {
  try {
    const headers = await getAuthHeaders(clientId, user);
    const response = await axios.put(`${baseURL}/auth/profile`, profileData, { headers });
    
    console.log("Update Profile Response:-->", response.data);
    return response.data;
  } catch (error) {
    console.error("Update Profile Error:", error);
    throw error;
  }
};
